/* eslint-disable prefer-arrow/prefer-arrow-functions */

import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';
import { DropFileDirective } from './dropfile.directive';
import { FileUploader } from './file-uploader';
import { FileService } from '../../file/file.service';
import { HttpService } from '../../http/http.service';
import { DialogsService } from '../../../dialogs/dialogs.service';

@Component({
    selector: 'file-uploader-component',
    templateUrl: './file-uploader.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DropFileDirective, MatButtonModule, MatIconModule, MatProgressBarModule, TranslocoModule]
})
export class FileUploaderComponent extends FileUploader implements OnInit {

    @Input() acceptedExtensions: string[] = [];
    @Input() control: FormControl; // required
    @Input() filePattern: RegExp;
    @Input() caption: string;
    @Input() embedded: boolean = true;
    @Input() extraPath: string = '';

    type: string;
    filename: string;
    base64: string;
    imageUrl: string;
    isImage: boolean = false;
    loading: boolean = false;

    previewedFile: {
        type: string;
        filename: string;
        base64: string;
        imageUrl: string;
        isImage: boolean;
        serverName: string;
    } = {type: null, filename: null, base64: null, imageUrl: null, isImage: false, serverName: null};

    dragOverCounter = 0;
    isDragOver = false;

    constructor(
        private fileService: FileService,
        private httpService: HttpService,
        private dialogService: DialogsService,
        private tr: TranslocoService,
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        const value: string = this.control.value;

        if (!!value) {
            this.filename = this.fileService.getFileNameFromCOSPath(value);       // 'file_name.example'
            if (value.startsWith('data:')) {
                this.base64 = value;
                const mimetype = this.fileService.getFileType(this.base64);
                if (mimetype) {
                    this.type = mimetype[1];
                    if (mimetype[0].startsWith('image')) {
                        this.isImage = true;
                    }
                }

            } else if (value.startsWith(':')) {
                this.loading = true;
                try {
                    const file = await firstValueFrom(this.fileService.download(value, { height: 120 }));
                    if (file) {
                        this.base64 = file;
                        const mimetype = this.fileService.getFileType(this.base64);
                        if (mimetype) {
                            this.type = mimetype[1];
                            if (mimetype[0].startsWith('image')) {
                                this.isImage = true;
                            }
                        }
                    }
                } finally {
                    this.loading = false;
                }
            } else {
                this.isImage = true;
                this.filename = undefined;
                this.imageUrl = value.startsWith('http') ? value : `${this.httpService.getBaseUrl()}${value}`;
                this.toDataUrl(this.imageUrl, (myBase64: string) => {
                    this.base64 = myBase64;
                    const mimetype = this.fileService.getFileType(this.base64);
                        if (mimetype) {
                            this.type = mimetype[1];
                        }
                });
            }
        }
    }

    uploadFile(file: File) {
        if (this.control.disabled) {
            return;
        }
        this.loading = true;
        const split = file.name.split('.');
        const ext = split[split.length - 1];
        const filename = split.slice(0, split.length - 1).join('.');
        if (this.acceptedExtensions.length > 0 && !this.acceptedExtensions.map(ex => ex.toLowerCase()).includes(ext.toLowerCase())) {
            this.dialogService.openErrorDialog({
                title: this.tr.translate('invalidFormat'),
                message: this.tr.translate('invalidExtension'),
            });
            this.loading = false;
        } else if (this.filePattern && !this.filePattern.test(filename)) {
            this.dialogService.openErrorDialog({
                title: this.tr.translate('invalidFormat'),
                message: this.tr.translate('invalidRegex'),
            });
            this.loading = false;
        } else {
            const reader = new FileReader();
            reader.onload = async (e) => {
                if (file.type.split('/')[0].startsWith('image')) {
                    this.isImage = true;
                } else {
                    this.isImage = false;
                }
                this.type = ext;
                this.filename = file.name;
                this.base64 = String((e.target as FileReader).result);
                try {
                    if (this.embedded) {
                        this.control.setValue(this.base64);
                    } else {
                        const _filename = await firstValueFrom(this.fileService.upload(file.name, this.base64, this.extraPath));
                        this.control.setValue(_filename);
                    }
                } catch (error) {
                    console.error(error);
                } finally {
                    this.loading = false;
                }
            };

            reader.onerror = (e) => {
                this.loading = false;
            };

            reader.readAsDataURL(file);
        }
    }

    previewFile(event: MouseEvent, i: number) {
        event.stopPropagation();
        const image = new Image();
        image.src = this.base64;
        this.previewedFile.base64 = this.base64;
        this.previewedFile.filename = this.filename;
        this.previewedFile.imageUrl = this.imageUrl;
        this.previewedFile.isImage = this.isImage;
        this.previewedFile.type = this.type;
        this.dialogService.openPreviewFileDialog(this.previewedFile,  this.getDialogWidth(image.width),  this.getDialogHeight(image.height));
    }

    downloadFile(event: MouseEvent) {
        event.stopPropagation();
        const e = document.createElement('a');
        e.target = this.imageUrl ? '_blank' : e.target;
        e.download = this.filename || 'unnamed_file';
        e.href = this.base64;
        e.click();
    }

    toDataUrl(url: string, callback) {
        const xhr = new XMLHttpRequest();
        xhr.onload = function() {
            const reader = new FileReader();
            reader.onloadend = function() {
                callback(reader.result);
            };
            reader.readAsDataURL(xhr.response);
        };
        xhr.open('GET', url);
        xhr.responseType = 'blob';
        xhr.send();
    }

    removeFile(event: MouseEvent) {
        event.stopPropagation();
        this.control.setValue(undefined);
        this.isImage = false;
        this.base64 = '';
        this.imageUrl = '';
        this.type = '';
        this.filename = '';
    }

    inputClick(input) {
        input.click();
        this.control.markAsTouched();
    }

    getDialogWidth(width: number) {
        if (width > 1800) {
            return '90vw';
        } else if (width > 1500) {
            return '80vw';
        } else if (width > 1200) {
            return '70vw';
        } else if (width > 900) {
            return '60vw';
        } else if (width > 600) {
            return '50vw';
        } else if (width > 300) {
            return '40vw';
        } else if (width > 100) {
            return '30vw';
        } else {
            return '20vw';
        }
    }

    getDialogHeight(height: number) {
        if (height > 900) {
            return '90vh';
        } else if (height > 700) {
            return '80vh';
        } else if (height > 500) {
            return '70vh';
        } else if (height > 300) {
            return '60vh';
        } else if (height > 100) {
            return '35vh';
        } else {
            return '20vh';
        }
    }
}
